import React, {
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  type DefaultValues,
  type FieldValues,
  type Mode,
  type Path,
  type PathValue,
  useForm,
  UseFormWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Location, useBlocker } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  type SxProps,
  type Theme,
  useTheme
} from '@mui/material';
import { mergeSx } from 'merge-sx';
import * as yup from 'yup';

import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import Form from '@/components/Form/Form';
import { useSnackbar } from '@/hooks/useSnackbar';
import { yupValidationResolver } from '@/hooks/useYupValidationResolver';
import { height as appBarHeight } from '@/theme/components/MuiAppBar';
import { normalizePathname } from '@/utils/normalizePathname';

import CloseIcon from './assets/close.svg?react';

const MODAL_MARGIN_TOP = 2;
const MODAL_MARGIN_BOTTOM = 4;
const MODAL_MARGIN_HORIZONTAL = 4;
const MODAL_MAX_WIDTH = 1168;
const CLOSE_ICON_SIZE = 32;

export interface SettingsModalFormRef<T extends FieldValues> {
  setValue: (key: Path<T>, value: PathValue<T, Path<T>>) => void;
  reset: () => void;
  getValues: () => FieldValues;
  watch: UseFormWatch<T>;
}

interface SettingsModalFormProps<T extends FieldValues> {
  head: string | ReactElement;
  children: ReactNode;
  onSave?: (values: T) => Promise<void>;
  onClose?: () => void;
  defaultValues?: DefaultValues<T>;
  validation?: yup.Schema<T>;
  validationMode?: Mode;
  sx?: SxProps<Theme>;
  isSaving?: boolean;
  isOpen?: boolean;
  closeOnSave?: boolean;
  locationsMatch?: (opt: { currentLocation: Location; nextLocation: Location }) => boolean;
  routePattern?: string;
  className?: string;
  shouldConfirmBeforeLeave?: boolean;
}

const SettingsModalFormInner = <T extends FieldValues>(
  {
    onSave,
    onClose,
    head,
    defaultValues,
    validation,
    validationMode,
    sx,
    children,
    isOpen = true,
    closeOnSave = true,
    shouldConfirmBeforeLeave = true,
    locationsMatch,
    routePattern,
    className
  }: SettingsModalFormProps<T>,
  ref: React.Ref<SettingsModalFormRef<T>>
) => {
  const { t } = useTranslation(['settings', 'validation']);
  const { spacing } = useTheme();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const blockerBypassRef = useRef(false);
  const { showMessage } = useSnackbar();

  const resolver = useMemo(() => yupValidationResolver(validation), [validation]);
  const form = useForm({ defaultValues: defaultValues, mode: validationMode, reValidateMode: 'onChange', resolver });

  useImperativeHandle(ref, () => ({
    setValue: (key: Path<T>, value: PathValue<T, Path<T>>) => {
      form.setValue(key as Path<T>, value as PathValue<T, Path<T>>, {
        shouldDirty: true,
        shouldTouch: true
      });
    },
    reset: () => form.reset(),
    getValues: () => form.getValues(),
    watch: form.watch
  }));
  const {
    formState: { dirtyFields },
    getValues,
    reset
  } = form;

  const hasDirtyFields = !!Object.keys(dirtyFields).length;

  const onCloseCallback = useCallback(
    (callback?: () => void) => {
      if (!hasDirtyFields || !shouldConfirmBeforeLeave) {
        callback?.();

        return false;
      }

      setShowConfirmationModal(true);

      return true;
    },
    [hasDirtyFields, shouldConfirmBeforeLeave]
  );

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!locationsMatch && !routePattern) return false;

    if (blockerBypassRef.current) {
      blockerBypassRef.current = false;

      return false;
    }

    let isSameLocation;
    if (locationsMatch) {
      isSameLocation = locationsMatch({ currentLocation, nextLocation });
    } else if (routePattern) {
      const isSamePathname = currentLocation.pathname === nextLocation.pathname;
      const isSameNormalizedPathname =
        normalizePathname(routePattern, currentLocation.pathname) ===
        normalizePathname(routePattern, nextLocation.pathname);

      isSameLocation = isSamePathname || isSameNormalizedPathname;
    }

    return !isSameLocation && onCloseCallback();
  });

  const onSaveCallback = useCallback(
    async (forceClose: boolean = false) => {
      const values = getValues();
      await onSave?.(values);
      blockerBypassRef.current = true;
      if (closeOnSave || forceClose) {
        onClose?.();
      } else {
        reset(values, { keepValues: true });
      }
    },
    [getValues, onSave, closeOnSave, onClose, reset]
  );

  const onNavigationConfirm = useCallback(async () => {
    await onSaveCallback(true);
    setShowConfirmationModal(false);
    blocker.proceed?.();
  }, [blocker, onSaveCallback]);

  const onNavigationCancel = useCallback(() => {
    setShowConfirmationModal(false);
    blocker.reset?.();
  }, [blocker]);

  const onNavigationFailedConfirm = useCallback(() => {
    showMessage(t('validation:alerts.failed'));
    onNavigationCancel();
  }, [onNavigationCancel, showMessage, t]);

  const onNavigationDiscard = useCallback(() => {
    form?.reset();
    setShowConfirmationModal(false);
    setTimeout(() => {
      blocker.proceed?.();
      onClose?.();
    }, 100);
  }, [form, onClose, blocker]);

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (hasDirtyFields) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [hasDirtyFields]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => onCloseCallback(onClose)}
        sx={mergeSx(
          {
            '& .SettingsModalForm-root': {
              mt: appBarHeight + MODAL_MARGIN_TOP,
              mb: MODAL_MARGIN_BOTTOM,
              mx: MODAL_MARGIN_HORIZONTAL,
              height: `calc(100vh - ${spacing(appBarHeight + MODAL_MARGIN_TOP + MODAL_MARGIN_BOTTOM)})`,
              width: `calc(100vw - ${spacing(MODAL_MARGIN_HORIZONTAL * 2)})`,
              maxWidth: MODAL_MAX_WIDTH
            }
          },
          sx
        )}
        PaperProps={{
          component: Form,
          className: 'SettingsModalForm-root',
          form,
          onSubmit: onSaveCallback
        }}
        className={className}
      >
        <DialogTitle
          component={Stack}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          bgcolor="background.default"
          sx={{
            p: spacing(1.5, 2, 2.25)
          }}
        >
          <Box flexGrow={1}>{head}</Box>
          <IconButton
            title={t('orgSettings.talentRegistrationForm.close')}
            sx={{ fontSize: CLOSE_ICON_SIZE, p: 0.25 }}
            onClick={() => onCloseCallback(onClose)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {children}
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        open={showConfirmationModal}
        onConfirm={form.handleSubmit(onNavigationConfirm, onNavigationFailedConfirm)}
        onClose={onNavigationCancel}
        onDiscard={onNavigationDiscard}
        discardButtonLabel={t('settings.discardButton')}
        title={t('settings.unsavedChangesTitle')}
      />
    </>
  );
};

const SettingsModalForm = forwardRef(SettingsModalFormInner);

SettingsModalForm.displayName = 'SettingsModalForm';

export default SettingsModalForm;
